import * as React from 'react'
import {
    Box,
    Checkbox,
    Chip,
    Divider,
    Grid,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import CastleRoundedIcon from '@mui/icons-material/CastleRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded'

import nativeVillageShopCatalog from '../data/nativeVillageShopCatalog.json'
import CompactAssetIcon from './CompactAssetIcon'
import {
    getNativeVillageItemIconType,
    getNativeVillageItemIconUrl
} from './gameAssetIcons'

const PLUGIN_KEY = 'nativevillage'
const SETTINGS_KEY = 'nativeVillageSettings'
const EMPTY_PLUGIN_CONFIG = Object.freeze({})

const PRIMARY_CARD_SX = {
    backgroundColor: 'rgba(18, 29, 48, 0.82)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '22px',
    boxShadow: '0 16px 34px rgba(0,0,0,0.18)'
}

const SECTION_CARD_SX = {
    backgroundColor: 'rgba(18, 29, 48, 0.72)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '18px'
}

const ITEM_CARD_SX = {
    height: '100%',
    p: 1.45,
    borderRadius: '14px',
    border: '1px solid rgba(129, 146, 178, 0.14)',
    backgroundColor: 'rgba(20, 31, 52, 0.54)'
}

const FIELD_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        minHeight: 42,
        borderRadius: '10px',
        backgroundColor: 'rgba(23, 34, 55, 0.72)'
    },
    '& .MuiInputBase-input': {
        fontSize: '0.98rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

const TOGGLE_SWITCH_SX = {
    width: 48,
    height: 29,
    p: 0,
    '& .MuiSwitch-switchBase': {
        p: '2.5px',
        '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#ffffff'
        },
        '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#2f7cff',
            opacity: 1
        }
    },
    '& .MuiSwitch-thumb': {
        width: 24,
        height: 24,
        boxShadow: 'none'
    },
    '& .MuiSwitch-track': {
        opacity: 1,
        borderRadius: '999px',
        backgroundColor: 'rgba(111, 123, 143, 0.9)'
    }
}

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const normalizeDigits = (value, fallback = '') => {
    const normalized = String(value ?? '').replace(/[^\d]/g, '')
    return normalized || fallback
}

const normalizePositiveDigits = (value, fallback = '1') => {
    const normalized = normalizeDigits(value)
    const parsed = Number.parseInt(normalized, 10)
    return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : fallback
}

const createDefaultPurchaseMap = location =>
    Object.fromEntries(
        location.items.map(item => [
            String(item.packageID),
            {
                enabled: false,
                amount: '1',
                sell: {
                    enabled: false
                }
            }
        ])
    )

const isSellableNativeVillageItem = item =>
    item?.packageType === 'constructionItem' && Number.isFinite(Number(item?.constructionItemID))

const normalizeNativeVillageSettings = pluginConfig => {
    const source = pluginConfig?.[SETTINGS_KEY] && typeof pluginConfig[SETTINGS_KEY] === 'object' ?
        pluginConfig[SETTINGS_KEY] :
        {}
    const sourceLocations = source.locations && typeof source.locations === 'object' ? source.locations : {}

    return {
        checkIntervalMinutes: normalizeDigits(source.checkIntervalMinutes, '30'),
        maxPurchasesPerRun: normalizeDigits(source.maxPurchasesPerRun, '3'),
        sellEnabled: source.sellEnabled === true,
        locations: Object.fromEntries(
            nativeVillageShopCatalog.locations.map(location => {
                const sourceLocation = sourceLocations[location.key] && typeof sourceLocations[location.key] === 'object' ?
                    sourceLocations[location.key] :
                    {}
                const sourcePurchases = sourceLocation.purchases && typeof sourceLocation.purchases === 'object' ?
                    sourceLocation.purchases :
                    {}
                const purchases = createDefaultPurchaseMap(location)

                location.items.forEach(item => {
                    const key = String(item.packageID)
                    const sourcePurchase = sourcePurchases[key] && typeof sourcePurchases[key] === 'object' ?
                        sourcePurchases[key] :
                        {}

                    purchases[key] = {
                        enabled: sourcePurchase.enabled === true,
                        amount: normalizeDigits(sourcePurchase.amount, '1'),
                        sell: {
                            enabled: isSellableNativeVillageItem(item) && (
                                sourcePurchase.sell === true ||
                                sourcePurchase.sell?.enabled === true
                            )
                        }
                    }
                })

                return [
                    location.key,
                    {
                        enabled: typeof sourceLocation.enabled === 'boolean' ?
                            sourceLocation.enabled :
                            pluginConfig?.[location.optionKey] !== false,
                        purchases
                    }
                ]
            })
        )
    }
}

const formatNumber = value => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed.toLocaleString('en-US') : String(value ?? '')
}

const getStatusByLocationKey = runtimeStatus => {
    const rows = Array.isArray(runtimeStatus) ? runtimeStatus : []

    return new Map(
        rows
            .filter(row => row && typeof row === 'object')
            .map(row => [String(row.key ?? '').trim(), row])
    )
}

function StyledSwitch({ checked, onChange, disabled = false }) {
    return (
        <Switch
            checked={checked}
            disabled={disabled}
            onChange={event => onChange(event.target.checked)}
            sx={TOGGLE_SWITCH_SX}
        />
    )
}

export default function NativeVillageSettingsPanel({ userPlugins, onChange, runtimeStatus }) {
    const pluginConfig = React.useMemo(
        () => userPlugins?.[PLUGIN_KEY] ?? EMPTY_PLUGIN_CONFIG,
        [userPlugins]
    )
    const settings = React.useMemo(
        () => normalizeNativeVillageSettings(pluginConfig),
        [pluginConfig]
    )
    const statusByLocationKey = React.useMemo(
        () => getStatusByLocationKey(runtimeStatus),
        [runtimeStatus]
    )

    const commit = React.useCallback(nextSettings => {
        const normalizedSettings = normalizeNativeVillageSettings({
            ...pluginConfig,
            [SETTINGS_KEY]: nextSettings
        })
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[PLUGIN_KEY] ??= {}
        nextPlugins[PLUGIN_KEY][SETTINGS_KEY] = normalizedSettings

        nativeVillageShopCatalog.locations.forEach(location => {
            nextPlugins[PLUGIN_KEY][location.optionKey] =
                normalizedSettings.locations[location.key]?.enabled !== false
        })

        onChange(nextPlugins)
    }, [onChange, pluginConfig, userPlugins])

    const updateSettings = React.useCallback(mutator => {
        const nextSettings = cloneValue(settings)
        mutator(nextSettings)
        commit(nextSettings)
    }, [commit, settings])

    const selectedCount = nativeVillageShopCatalog.locations.reduce((total, location) => {
        const purchases = settings.locations[location.key]?.purchases ?? {}
        return total + location.items.filter(item => purchases[String(item.packageID)]?.enabled).length
    }, 0)
    const sellCount = nativeVillageShopCatalog.locations.reduce((total, location) => {
        const purchases = settings.locations[location.key]?.purchases ?? {}
        return total + location.items.filter(item => {
            const purchase = purchases[String(item.packageID)]
            return settings.sellEnabled && purchase?.enabled && purchase?.sell?.enabled
        }).length
    }, 0)

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.4 }}>
            <Paper sx={PRIMARY_CARD_SX}>
                <Box sx={{ p: { xs: 2.2, md: 3.2 } }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.6}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                    >
                        <Stack spacing={0.75}>
                            <Stack direction="row" spacing={1.3} alignItems="center">
                                <StorefrontRoundedIcon sx={{ color: '#f5f7ff', fontSize: 22 }} />
                                <Typography sx={{ color: '#f5f7ff', fontSize: '1.55rem', fontWeight: 800 }}>
                                    Native Village Settings
                                </Typography>
                            </Stack>
                            <Typography sx={{ color: '#9badcb', fontSize: '1rem', maxWidth: 760 }}>
                                Select shop items per kingdom. Purchases run only for enabled locations and selected packages.
                            </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1.1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{
                                    px: 1.1,
                                    py: 0.75,
                                    borderRadius: '14px',
                                    border: '1px solid rgba(129, 146, 178, 0.18)',
                                    backgroundColor: 'rgba(56, 73, 103, 0.62)'
                                }}
                            >
                                <Typography sx={{ color: '#eef4ff', fontSize: '0.92rem', fontWeight: 800 }}>
                                    Sell
                                </Typography>
                                <StyledSwitch
                                    checked={settings.sellEnabled}
                                    onChange={checked => updateSettings(nextSettings => {
                                        nextSettings.sellEnabled = checked
                                    })}
                                />
                            </Stack>
                            <Chip
                                icon={<Inventory2RoundedIcon sx={{ color: 'inherit !important' }} />}
                                label={`${selectedCount} selected / ${sellCount} sell`}
                                sx={{
                                    backgroundColor: 'rgba(56, 73, 103, 0.78)',
                                    color: '#eef4ff',
                                    fontWeight: 800,
                                    border: '1px solid rgba(129,146,178,0.2)'
                                }}
                            />
                        </Stack>
                    </Stack>

                    <Grid container spacing={2} sx={{ mt: 2.6 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Check interval minutes (min 5)"
                                value={settings.checkIntervalMinutes}
                                onChange={event => updateSettings(nextSettings => {
                                    nextSettings.checkIntervalMinutes = normalizeDigits(event.target.value)
                                })}
                                onBlur={() => updateSettings(nextSettings => {
                                    nextSettings.checkIntervalMinutes = normalizePositiveDigits(nextSettings.checkIntervalMinutes, '30')
                                })}
                                sx={FIELD_INPUT_SX}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Max purchases per run"
                                value={settings.maxPurchasesPerRun}
                                onChange={event => updateSettings(nextSettings => {
                                    nextSettings.maxPurchasesPerRun = normalizeDigits(event.target.value)
                                })}
                                onBlur={() => updateSettings(nextSettings => {
                                    nextSettings.maxPurchasesPerRun = normalizePositiveDigits(nextSettings.maxPurchasesPerRun, '3')
                                })}
                                sx={FIELD_INPUT_SX}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            {nativeVillageShopCatalog.locations.map(location => {
                const locationSettings = settings.locations[location.key]
                const locationEnabled = locationSettings?.enabled !== false
                const statusRow = statusByLocationKey.get(location.key)
                const currentLevel = Number(statusRow?.level)
                const hasLevel = Number.isFinite(currentLevel)
                const resourceText = statusRow?.kingdomResource == null ?
                    `${location.resourceLabel} unknown` :
                    `${formatNumber(statusRow.kingdomResource)} ${location.resourceLabel}`
                const locationSelectedCount = location.items
                    .filter(item => locationSettings?.purchases?.[String(item.packageID)]?.enabled)
                    .length
                const locationSellCount = location.items
                    .filter(item => {
                        const purchase = locationSettings?.purchases?.[String(item.packageID)]
                        return settings.sellEnabled && purchase?.enabled && purchase?.sell?.enabled
                    })
                    .length

                return (
                    <Paper key={location.key} sx={SECTION_CARD_SX}>
                        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1.4}
                                alignItems={{ xs: 'stretch', md: 'center' }}
                                justifyContent="space-between"
                            >
                                <Stack direction="row" spacing={1.1} alignItems="center">
                                    <CastleRoundedIcon sx={{ color: '#8fb4ff' }} />
                                    <Box>
                                        <Typography sx={{ color: '#f5f7ff', fontSize: '1.22rem', fontWeight: 800 }}>
                                            {location.label}
                                        </Typography>
                                        <Stack direction="row" spacing={0.8} sx={{ mt: 0.65, flexWrap: 'wrap' }}>
                                            <Chip
                                                size="small"
                                                label={hasLevel ? `Level ${currentLevel}` : 'Level unknown'}
                                                sx={{
                                                    color: '#dfe8ff',
                                                    backgroundColor: 'rgba(65, 85, 124, 0.64)',
                                                    fontWeight: 800
                                                }}
                                            />
                                            <Chip
                                                size="small"
                                                label={resourceText}
                                                sx={{
                                                    color: '#dfe8ff',
                                                    backgroundColor: 'rgba(65, 85, 124, 0.64)',
                                                    fontWeight: 800
                                                }}
                                            />
                                            <Chip
                                                size="small"
                                                label={`${locationSelectedCount} selected / ${locationSellCount} sell`}
                                                sx={{
                                                    color: '#dfe8ff',
                                                    backgroundColor: 'rgba(65, 85, 124, 0.64)',
                                                    fontWeight: 800
                                                }}
                                            />
                                        </Stack>
                                    </Box>
                                </Stack>

                                <StyledSwitch
                                    checked={locationEnabled}
                                    onChange={checked => updateSettings(nextSettings => {
                                        nextSettings.locations[location.key].enabled = checked
                                    })}
                                />
                            </Stack>

                            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.07)' }} />

                            <Grid container spacing={1.4}>
                                {location.items.map(item => {
                                    const purchaseKey = String(item.packageID)
                                    const purchase = locationSettings?.purchases?.[purchaseKey] ?? {
                                        enabled: false,
                                        amount: '1',
                                        sell: { enabled: false }
                                    }
                                    const levelLocked = hasLevel && currentLevel < Number(item.requiredLevel)
                                    const itemSellable = isSellableNativeVillageItem(item)
                                    const itemIconUrl = getNativeVillageItemIconUrl(item)
                                    const itemIconType = getNativeVillageItemIconType(item)

                                    return (
                                        <Grid key={purchaseKey} item xs={12} md={6} xl={4}>
                                            <Paper sx={ITEM_CARD_SX}>
                                                <Stack spacing={1.2} sx={{ height: '100%' }}>
                                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                                        <Checkbox
                                                            checked={purchase.enabled}
                                                            disabled={!locationEnabled}
                                                            onChange={event => updateSettings(nextSettings => {
                                                                nextSettings.locations[location.key].purchases[purchaseKey].enabled = event.target.checked
                                                            })}
                                                            sx={{
                                                                p: 0.3,
                                                                color: '#86a7ff',
                                                                '&.Mui-checked': {
                                                                    color: '#86a7ff'
                                                                }
                                                            }}
                                                        />
                                                        <CompactAssetIcon
                                                            iconUrl={itemIconUrl}
                                                            itemType={itemIconType}
                                                            size={64}
                                                            sx={{ mt: -0.4 }}
                                                        />
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                                                                {item.label}
                                                            </Typography>
                                                            <Typography sx={{ color: '#9badcb', fontSize: '0.88rem', mt: 0.35 }}>
                                                                {item.rewardLabel}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>

                                                    <Stack direction="row" spacing={0.7} sx={{ flexWrap: 'wrap' }}>
                                                        <Chip
                                                            size="small"
                                                            label={`Lvl ${item.requiredLevel}`}
                                                            sx={{
                                                                color: levelLocked ? '#ffb36d' : '#cfe1ff',
                                                                backgroundColor: levelLocked ? 'rgba(255, 152, 0, 0.13)' : 'rgba(80, 104, 149, 0.45)',
                                                                border: levelLocked ? '1px solid rgba(255, 152, 0, 0.28)' : '1px solid rgba(129,146,178,0.14)',
                                                                fontWeight: 800
                                                            }}
                                                        />
                                                        <Chip
                                                            size="small"
                                                            label={`${formatNumber(item.price)} ${location.resourceLabel}`}
                                                            sx={{
                                                                color: '#cfe1ff',
                                                                backgroundColor: 'rgba(80, 104, 149, 0.45)',
                                                                fontWeight: 800
                                                            }}
                                                        />
                                                        <Chip
                                                            size="small"
                                                            label={`PID ${item.packageID}`}
                                                            sx={{
                                                                color: '#cfe1ff',
                                                                backgroundColor: 'rgba(80, 104, 149, 0.45)',
                                                                fontWeight: 800
                                                            }}
                                                        />
                                                    </Stack>

                                                    {itemSellable && (
                                                        <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            sx={{
                                                                px: 1,
                                                                py: 0.85,
                                                                borderRadius: '12px',
                                                                backgroundColor: 'rgba(12, 20, 34, 0.38)',
                                                                border: '1px solid rgba(129, 146, 178, 0.12)'
                                                            }}
                                                        >
                                                            <Typography sx={{ color: '#dfe8ff', fontSize: '0.9rem', fontWeight: 800 }}>
                                                                Sell after buy
                                                            </Typography>
                                                            <StyledSwitch
                                                                checked={purchase.sell?.enabled === true}
                                                                onChange={checked => updateSettings(nextSettings => {
                                                                    nextSettings.locations[location.key].purchases[purchaseKey].sell.enabled = checked
                                                                })}
                                                                disabled={!settings.sellEnabled || !locationEnabled || !purchase.enabled}
                                                            />
                                                        </Stack>
                                                    )}

                                                    <TextField
                                                        label="Amount per run"
                                                        value={purchase.amount}
                                                        disabled={!locationEnabled || !purchase.enabled}
                                                        onChange={event => updateSettings(nextSettings => {
                                                            nextSettings.locations[location.key].purchases[purchaseKey].amount =
                                                                normalizeDigits(event.target.value)
                                                        })}
                                                        onBlur={() => updateSettings(nextSettings => {
                                                            nextSettings.locations[location.key].purchases[purchaseKey].amount =
                                                                normalizePositiveDigits(
                                                                    nextSettings.locations[location.key].purchases[purchaseKey].amount,
                                                                    '1'
                                                                )
                                                        })}
                                                        sx={{ ...FIELD_INPUT_SX, mt: 'auto' }}
                                                    />
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                    )
                                })}
                            </Grid>
                        </Box>
                    </Paper>
                )
            })}
        </Box>
    )
}
